import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import ContactForm from '../components/landing/ContactForm';
import { SOLUTIONS_PAGE, CLIENTS, COLORS, CONTACT } from '../data/siteData';
import { X, Check } from 'lucide-react';

// Hero background image for solutions page
const SOLUTIONS_HERO_BG = "https://customer-assets.emergentagent.com/job_knygomatai-clone/artifacts/stpmz5v8_a-staircase-in-a-library-bookshelves-full-of-book-2026-01-05-05-42-44-utc.jpg";

// Lithuanian flag colors for client type cards
const CLIENT_TYPE_COLORS = [
  { bg: COLORS.yellow, text: COLORS.dark },
  { bg: COLORS.green, text: '#FFFFFF' },
  { bg: COLORS.red, text: '#FFFFFF' },
  { bg: COLORS.dark, text: '#FFFFFF' },
];

// Inline Logo Carousel component for Solutions page
const LogoCarouselInline = () => {
  const scrollRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationId;
    let scrollPosition = 0;

    const scroll = () => {
      if (!isHovered) {
        scrollPosition += 0.5;
        if (scrollPosition >= scrollContainer.scrollWidth / 2) {
          scrollPosition = 0;
        }
        scrollContainer.scrollLeft = scrollPosition;
      }
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [isHovered]);

  const duplicatedLogos = [...CLIENTS.logos, ...CLIENTS.logos];

  return (
    <div 
      className="overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div 
        ref={scrollRef}
        className="flex gap-8 overflow-x-hidden"
        style={{ scrollBehavior: 'auto' }}
      >
        {duplicatedLogos.map((logo, index) => (
          <div 
            key={index}
            className="flex-shrink-0 w-24 h-24 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300"
          >
            <img
              src={logo.image}
              alt={`${logo.name} bibliotekos logotipas`}
              className="max-w-full max-h-full object-contain"
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const SolutionsPage = () => {
  const [showContactForm, setShowContactForm] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.background }}>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{SOLUTIONS_PAGE.metaTitle}</title>
        <meta name="description" content={SOLUTIONS_PAGE.metaDescription} />
        <link rel="canonical" href="https://knygomatai.lt/sprendimai-bibliotekoms" />
        <meta property="og:title" content={SOLUTIONS_PAGE.metaTitle} />
        <meta property="og:description" content={SOLUTIONS_PAGE.metaDescription} />
        <meta property="og:url" content="https://knygomatai.lt/sprendimai-bibliotekoms" />
      </Helmet>

      <Header 
        onDemoClick={() => setShowContactForm(true)} 
        scrollToSection={scrollToSection}
        isTransparent={true}
      />
      
      <main>
        {/* Hero Section with Background Image */}
        <section className="relative min-h-[60vh] flex items-center">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${SOLUTIONS_HERO_BG})` }}
          />
          <div className="absolute inset-0 bg-black/50" />
          <div 
            className="absolute inset-0"
            style={{ backgroundColor: COLORS.green, opacity: 0.15, mixBlendMode: 'multiply' }}
          />

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
            <div className="max-w-4xl">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6 text-white">
                {SOLUTIONS_PAGE.title}
              </h1>
              <p className="text-lg leading-relaxed text-white/90">
                {SOLUTIONS_PAGE.subtitle}
              </p>
            </div>
          </div>
        </section>

        {/* Solutions Grid */}
        <section className="py-20" style={{ backgroundColor: COLORS.background }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: COLORS.dark }}>
                {SOLUTIONS_PAGE.sectionTitle}
              </h2>
              <p className="text-lg" style={{ color: COLORS.gray }}>
                {SOLUTIONS_PAGE.sectionSubtitle}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {SOLUTIONS_PAGE.solutions.map((solution, index) => (
                <article 
                  key={index}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-gray-100 flex items-center justify-center p-4">
                    <img
                      src={solution.image}
                      alt={solution.alt || solution.title}
                      className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-4" style={{ color: COLORS.dark }}>
                      {solution.title}
                    </h3>
                    <ul className="space-y-2 mb-4">
                      {solution.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start space-x-2">
                          <div 
                            className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ backgroundColor: COLORS.green + '20' }}
                          >
                            <Check className="w-3 h-3" style={{ color: COLORS.green }} />
                          </div>
                          <span className="text-sm" style={{ color: COLORS.gray }}>
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                    {/* SEO Description Text */}
                    {solution.seoDescription && (
                      <p className="text-sm leading-relaxed" style={{ color: COLORS.gray }}>
                        {solution.seoDescription}
                      </p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Local Signal Section */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-base leading-relaxed" style={{ color: COLORS.gray }}>
              {SOLUTIONS_PAGE.localSignal}
            </p>
          </div>
        </section>

        {/* Client Types - Lithuanian Flag Colors */}
        <section className="py-20" style={{ backgroundColor: COLORS.background }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-12 text-center" style={{ color: COLORS.dark }}>
              {SOLUTIONS_PAGE.clientTypes.title}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {SOLUTIONS_PAGE.clientTypes.items.map((item, index) => {
                const colorScheme = CLIENT_TYPE_COLORS[index % CLIENT_TYPE_COLORS.length];
                return (
                  <div 
                    key={index}
                    className="p-8 rounded-xl text-center transition-all duration-300 hover:scale-105 hover:shadow-lg"
                    style={{ backgroundColor: colorScheme.bg, color: colorScheme.text }}
                  >
                    <p className="font-semibold text-lg">{item}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* References - Scrolling Carousel */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: COLORS.dark }}>
                {SOLUTIONS_PAGE.references.title}
              </h2>
              <p className="text-lg" style={{ color: COLORS.gray }}>
                {SOLUTIONS_PAGE.references.subtitle}
              </p>
            </div>
            <LogoCarouselInline />
          </div>
        </section>

        {/* SEO FAQ Section */}
        <section className="py-20" style={{ backgroundColor: COLORS.background }}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-12 text-center" style={{ color: COLORS.dark }}>
              {SOLUTIONS_PAGE.seoFaq.title}
            </h2>
            <div className="space-y-8">
              {SOLUTIONS_PAGE.seoFaq.items.map((faq, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-semibold mb-3" style={{ color: COLORS.dark }}>
                    {faq.question}
                  </h3>
                  <p className="text-base leading-relaxed" style={{ color: COLORS.gray }}>
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: COLORS.dark }}>
              {SOLUTIONS_PAGE.cta.title}
            </h2>
            <p className="text-lg mb-8" style={{ color: COLORS.gray }}>
              {SOLUTIONS_PAGE.cta.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => scrollToSection('contact')}
                className="px-8 py-3 text-base font-medium text-white rounded-lg transition-all hover:opacity-90"
                style={{ backgroundColor: COLORS.green }}
              >
                {SOLUTIONS_PAGE.cta.button1}
              </button>
              <button
                onClick={() => setShowContactForm(true)}
                className="px-8 py-3 text-base font-medium rounded-lg transition-all hover:opacity-90 border-2"
                style={{ color: COLORS.dark, borderColor: COLORS.lightGray }}
                data-testid="solutions-demo-btn"
              >
                {SOLUTIONS_PAGE.cta.button2}
              </button>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <ContactForm />
      </main>

      <Footer />
      
      {/* Demo Request Modal */}
      {showContactForm && (
        <div 
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setShowContactForm(false)}
        >
          <div 
            className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold" style={{ color: COLORS.dark }}>
                  {CONTACT.title}
                </h3>
                <button 
                  onClick={() => setShowContactForm(false)}
                  className="hover:opacity-70 transition-opacity"
                  style={{ color: COLORS.gray }}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <ContactForm isModal onSuccess={() => setShowContactForm(false)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SolutionsPage;
